"use strict";
Object.defineProperty(exports, "__esModule", {
    value: true
});
Object.defineProperty(exports, "QuestionAnswerSchema", {
    enumerable: true,
    get: function() {
        return QuestionAnswerSchema;
    }
});
const _zod = require("zod");
const _utilsisomorphic = require("@performyard/utils-isomorphic");
//#region Per-question-type answer schemas
const TextQuestionAnswerSchema = _zod.z.object({
    type: _zod.z.literal("text"),
    answer: _zod.z.string()
});
const MultilineQuestionAnswerSchema = _zod.z.object({
    type: _zod.z.literal("multiline"),
    answer: _zod.z.string()
});
const DateQuestionAnswerSchema = _zod.z.object({
    type: _zod.z.literal("date"),
    answer: _zod.z.string().regex(/^\d{4}-\d{2}-\d{2}$/, "Invalid date format")
});
const NumericQuestionAnswerSchema = _zod.z.object({
    type: _zod.z.literal("numeric"),
    answer: _zod.z.number(),
    min: _zod.z.number().optional(),
    max: _zod.z.number().optional(),
    decimals: _zod.z.boolean().optional()
}).superRefine(({ answer, min, max, decimals }, ctx)=>{
    if (min != null && answer < min) {
        ctx.addIssue({
            code: _zod.z.ZodIssueCode.too_small,
            minimum: min,
            origin: "number",
            inclusive: true,
            message: `Answer must be greater than or equal to ${min}`
        });
    }
    if (max != null && answer > max) {
        ctx.addIssue({
            code: _zod.z.ZodIssueCode.too_big,
            maximum: max,
            origin: "number",
            inclusive: true,
            message: `Answer must be less than or equal to ${max}`
        });
    }
    if (decimals === false && !Number.isInteger(answer)) {
        ctx.addIssue({
            code: _zod.z.ZodIssueCode.custom,
            message: "Answer must be a whole number"
        });
    }
});
const MultipleChoiceQuestionAnswerSchema = _zod.z.object({
    type: _zod.z.literal("multiple-choice"),
    answer: _zod.z.string(),
    choices: _zod.z.array(_zod.z.string()).optional()
}).superRefine(({ answer, choices }, ctx)=>{
    const validChoices = choices ?? [];
    if (!validChoices.includes(answer)) {
        ctx.addIssue({
            code: _zod.z.ZodIssueCode.custom,
            message: "Answer is not a valid choice"
        });
    }
});
const MultipleSelectQuestionAnswerSchema = _zod.z.object({
    type: _zod.z.literal("multiple-select"),
    answer: _zod.z.array(_zod.z.string()),
    choices: _zod.z.array(_zod.z.string()).optional(),
    multiSelectMinimum: _zod.z.number().optional(),
    multiSelectMaximum: _zod.z.number().optional()
}).superRefine(({ choices, answer, multiSelectMinimum, multiSelectMaximum }, ctx)=>{
    const validChoices = choices ?? [];
    if (!answer.every((a)=>validChoices.includes(a))) {
        ctx.addIssue({
            code: _zod.z.ZodIssueCode.custom,
            message: "Answer has an invalid choice"
        });
    }
    const effectiveMax = multiSelectMaximum ?? validChoices.length;
    if (multiSelectMinimum != null && multiSelectMinimum > 0 && multiSelectMinimum === effectiveMax) {
        if (answer.length !== multiSelectMinimum) {
            ctx.addIssue({
                code: _zod.z.ZodIssueCode.custom,
                message: `Must select ${multiSelectMinimum} choice(s)`
            });
        }
    } else {
        if (multiSelectMinimum != null && multiSelectMinimum > 0 && answer.length < multiSelectMinimum) {
            ctx.addIssue({
                code: _zod.z.ZodIssueCode.too_small,
                minimum: multiSelectMinimum,
                origin: "array",
                inclusive: true,
                message: `Must select at least ${multiSelectMinimum} choice(s)`
            });
        }
        if (effectiveMax != null && answer.length > effectiveMax) {
            ctx.addIssue({
                code: _zod.z.ZodIssueCode.too_big,
                maximum: effectiveMax,
                origin: "array",
                inclusive: true,
                message: `Must select no more than ${effectiveMax} choice(s)`
            });
        }
    }
});
const RatingScaleQuestionAnswerSchema = _zod.z.object({
    type: _zod.z.literal("rating-scale"),
    answer: _zod.z.number().int()
});
const StarsQuestionAnswerSchema = _zod.z.object({
    type: _zod.z.literal("stars"),
    answer: _zod.z.number().int().min(0).max(5)
});
const InstructionsQuestionAnswerSchema = _zod.z.object({
    type: _zod.z.literal("instructions"),
    answer: _zod.z.literal("")
});
const CalculatorQuestionAnswerSchema = _zod.z.object({
    type: _zod.z.literal("calculator"),
    answer: _zod.z.union([
        _zod.z.number(),
        _zod.z.string()
    ])
});
//#region Goal Discussion
const GoalDiscussionAnswerItem = _zod.z.object({
    goalId: _utilsisomorphic.transformObjectId,
    message: _zod.z.string(),
    score: _zod.z.number().optional(),
    scaleValue: _zod.z.number().nullable().optional(),
    selectedOptionIndex: _zod.z.number().optional(),
    weight: _zod.z.union([
        _zod.z.number(),
        _zod.z.string()
    ]).optional()
});
const GoalDiscussionQuestionAnswerSchema = _zod.z.object({
    type: _zod.z.literal("goal-discussion"),
    answer: _zod.z.array(GoalDiscussionAnswerItem),
    scoringMin: _zod.z.number().optional(),
    scoringMax: _zod.z.number().optional()
}).superRefine(({ answer, scoringMin, scoringMax }, ctx)=>{
    answer.forEach((item, i)=>{
        if (item.score != null) {
            if (scoringMin != null && item.score < scoringMin) {
                ctx.addIssue({
                    code: _zod.z.ZodIssueCode.custom,
                    message: `Score must be between ${scoringMin ?? "-Infinity"} and ${scoringMax ?? "Infinity"}`,
                    path: [
                        "answer",
                        i,
                        "score"
                    ]
                });
            }
            if (scoringMax != null && item.score > scoringMax) {
                ctx.addIssue({
                    code: _zod.z.ZodIssueCode.custom,
                    message: `Score must be between ${scoringMin ?? "-Infinity"} and ${scoringMax ?? "Infinity"}`,
                    path: [
                        "answer",
                        i,
                        "score"
                    ]
                });
            }
        }
    });
});
//#region Goal Creation
const GoalCreationQuestionAnswerSchema = _zod.z.object({
    type: _zod.z.literal("goal-creation"),
    answer: _zod.z.array(_zod.z.object({
        goalId: _utilsisomorphic.transformObjectId
    }))
});
const QuestionAnswerSchema = _zod.z.discriminatedUnion("type", [
    TextQuestionAnswerSchema,
    MultilineQuestionAnswerSchema,
    DateQuestionAnswerSchema,
    NumericQuestionAnswerSchema,
    MultipleChoiceQuestionAnswerSchema,
    MultipleSelectQuestionAnswerSchema,
    RatingScaleQuestionAnswerSchema,
    StarsQuestionAnswerSchema,
    InstructionsQuestionAnswerSchema,
    CalculatorQuestionAnswerSchema,
    GoalDiscussionQuestionAnswerSchema,
    GoalCreationQuestionAnswerSchema
]);
